import { useEffect, useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/athos/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { Price } from 'libs/athos/components/Price'
import { generateSrcSet, getLowQualityPlaceholder } from 'libs/athos/components/ResultImage'
import { CompactQuickAdd } from 'libs/athos/components/CompactQuickAdd'

const IMAGE_WIDTHS = [160, 240, 320, 480]

// Compact, horizontal recommendation card for narrow contexts (mini cart).
// Image left / details right at 50/50 inside a 245/169 container.
// Badges, promolines, wishlist, colour swatches and rating are intentionally omitted.
export function CompactResultCard({ result, dataIndex = '0' }) {
  const isB2BCustomer = window.globals?.customer?.b2b
  const [variant] = result.variants
  const clickLogger = useBeaconLogClick()

  const url = useMemo(() => `/products/${result.handle}`, [result.handle])
  const isOutlet = result?.tags?.includes('IsOutlet:true') ?? false
  const imageSrc = result?.image || result?.images?.[0]?.src

  // Dynamically-rendered results need the shared lazyload instance to re-scan.
  useEffect(() => {
    if (window.lazyLoadInstance) window.lazyLoadInstance.update()
  }, [imageSrc])

  const handleLogClick = () => {
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(result)
    clickLogger(result)
  }

  return (
    <div
      className="group/card relative"
      id={`result__${result.handle}`}
      data-dl-track="select_item"
      data-dl-product-id={result.parentId}
      data-dl-index={dataIndex}
    >
      <div className="flex aspect-[245/169] w-full overflow-hidden rounded-lg">
        {/* Image half */}
        <a
          href={url}
          onClick={handleLogClick}
          aria-label={result.name}
          className="block h-full w-1/2 shrink-0"
        >
          {imageSrc && (
            <img
              className="lazy size-full object-cover object-bottom bg-page-secondary"
              src={getLowQualityPlaceholder(imageSrc)}
              data-src={imageSrc}
              data-srcset={generateSrcSet(imageSrc, IMAGE_WIDTHS)}
              data-sizes="25vw"
              alt={result.name || 'Product image'}
            />
          )}
        </a>

        {/* Details half */}
        <div className="flex h-full w-1/2 shrink-0 flex-col gap-y-0.375 bg-page-secondary p-1">
          <a
            href={url}
            onClick={handleLogClick}
            className="paragraph-xs font-medium line-clamp-2 link-fade"
            aria-label={result.name}
            dangerouslySetInnerHTML={{ __html: result.name }}
          />

          <div className="flex flex-row items-center justify-start">
            {isB2BCustomer ? (
              <b2b-price className="group/price flex gap-x-0.25" data-variant-id={variant.id}>
                <span
                  data-original-price
                  className="paragraph-xs hidden font-medium line-through group-data-[sale=true]/price:block"
                ></span>
                <span
                  data-b2b-price
                  className="paragraph-xs font-medium group-data-[sale=true]/price:text-brand-sale"
                ></span>
              </b2b-price>
            ) : (
              <Price
                price={variant.price}
                compareAtPrice={variant.compare_at_price}
                className="flex gap-x-0.25"
                isOutlet={isOutlet}
              />
            )}
          </div>

          <CompactQuickAdd result={result} className="mt-auto" />
        </div>
      </div>
    </div>
  )
}
